import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { EmailBlock } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { RowActions } from './RowActions';

export const HeadingBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}> = ({ block, isSelected, onSelect, onChange: _onChange, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;

  const tag = (block.headingTag || 'h2').toLowerCase();
  const headingText = block.content || '';
  const subHeading = block.subHeading || '';

  const renderHeading = () => {
    const sx = { m: 0, p: 0, wordBreak: 'break-word' } as const;
    switch (tag) {
      case 'h1': return <Typography variant="h4" sx={sx}>{headingText}</Typography>;
      case 'h2': return <Typography variant="h5" sx={sx}>{headingText}</Typography>;
      case 'h3': return <Typography variant="h6" sx={sx}>{headingText}</Typography>;
      case 'h4': return <Typography variant="subtitle1" sx={sx}>{headingText}</Typography>;
      case 'h5': return <Typography variant="subtitle2" sx={sx}>{headingText}</Typography>;
      case 'h6': return <Typography variant="body1" sx={sx}>{headingText}</Typography>;
      default: return <Typography variant="h5" sx={sx}>{headingText}</Typography>;
    }
  };

  return (
    <Box sx={common} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onSelect}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Heading : {block.title || 'Heading'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
    </Box>
  );
};

export const HeadingBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  if (draft.type !== 'heading') return null;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Heading</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Title</Typography>
          <input value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 12 }} />

          <label style={{ fontSize: 12 }}>Heading</label>
          <input value={draft.content || ''} onChange={(e) => setDraft({ ...draft, content: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }} />

          <label style={{ fontSize: 12 }}>Tag</label>
          <select value={draft.headingTag || 'h2'} onChange={(e) => setDraft({ ...draft, headingTag: e.target.value as any })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }}>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="h4">H4</option>
            <option value="h5">H5</option>
            <option value="h6">H6</option>
          </select>

          <label style={{ fontSize: 12 }}>Align</label>
          <select value={draft.align || 'left'} onChange={(e) => setDraft({ ...draft, align: e.target.value as any })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>

          <label style={{ fontSize: 12 }}>Font Color</label>
          <input
            type="color"
            value={draft.fontColor || '#333333'}
            onChange={(e) => setDraft({ ...draft, fontColor: e.target.value })}
            style={{ width: '100%', height: '40px', border: '1px solid #ccc', borderRadius: '6px', marginBottom: '8px' }}
          />

          <label style={{ fontSize: 12 }}>Sub Heading</label>
          <textarea value={draft.subHeading || ''} onChange={(e) => setDraft({ ...draft, subHeading: e.target.value })} style={{ width: '100%', minHeight: 60, padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }} />

          <label style={{ fontSize: 12 }}>Sub Heading Color</label>
          <input
            type="color"
            value={draft.subHeadingColor || '#555555'}
            onChange={(e) => setDraft({ ...draft, subHeadingColor: e.target.value })}
            style={{ width: '100%', height: '40px', border: '1px solid #ccc', borderRadius: '6px', marginBottom: '8px' }}
          />

          <label style={{ fontSize: 12 }}>Top Margin (px)</label>
          <input
            type="number"
            value={draft.topMargin || 0}
            onChange={(e) => setDraft({ ...draft, topMargin: parseInt(e.target.value) || 0 })}
            placeholder="0"
            style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};


